import useSWR, { mutate } from 'swr';
import useSWRMutation from 'swr/mutation';
import {
    CM2Dataset,
    DataProcessingLogDto,
    DatasetAutomationDetails,
    DatasetDetails,
    DatasetImportLogDto,
    DatasetNotesDto,
    DownloadFileResult,
    Site,
} from '@interfaces/central-monitoring';
import { AutomationTypes } from '@pages-clean/central-monitoring/dataset-hub/interfaces/hub-dataset.interface';
import { getData, getDataForFile, postData, ValidationErrorResponse } from '@apis/rest-client';

const endpoints = {
    getAll: (studyRef: string) => `CentralMonitoring/dataset/${studyRef}`,
    getActive: (studyRef: string) => `CentralMonitoring/dataset/active/${studyRef}`,
    getGrid: (studyRef: string) => `CentralMonitoring/dataset/grid/${studyRef}`,
    getSiteGrid: (studyRef: string) => `CentralMonitoring/dataset/grid/${studyRef}/site`,
    getControlsGrid: (studyRef: string) => `CentralMonitoring/dataset/grid/${studyRef}/controls`,
    getSites: (studyRef: string, datasetId: number) => `CentralMonitoring/dataset/${studyRef}/${datasetId}/sites`,
    getSiteAutomation: (studyRef: string, datasetId: number) =>
        `CentralMonitoring/${studyRef}/dataset/${datasetId}/automation/site`,
    getControlsAutomation: (studyRef: string, datasetId: number) =>
        `CentralMonitoring/${studyRef}/dataset/${datasetId}/automation/controls`,
    getDataProcessingLogs: (studyRef: string) => `CentralMonitoring/DataProcessingLogs/${studyRef}/`,
    getImportLogs: (studyRef: string, datasetId?: number) =>
        datasetId == null ? null : `CentralMonitoring/${studyRef}/dataset/${datasetId}/importerLogs`,
    downloadImportLog: (studyRef: string, logType: number, datasetId: number) =>
        `CentralMonitoring/${studyRef}/dataset/${datasetId}/downloadImportLog/${logType}`,
};

const DatasetApi = {
    useGetAll: (studyRef: string, skip = false) => {
        const enabled = !skip && !!studyRef;
        return useSWR<CM2Dataset[]>(enabled && endpoints.getAll(studyRef), getData);
    },
    invalidateGetAll: (studyRef: string) => {
        return mutate(endpoints.getAll(studyRef));
    },

    useGetActive: (studyRef: string, skip = false) => {
        const enabled = !skip && !!studyRef;
        return useSWR<CM2Dataset[]>(enabled && endpoints.getActive(studyRef), getData);
    },

    useGetNotes: (studyRef: string, datasetId: number | undefined) => {
        const url = datasetId ? `CentralMonitoring/${studyRef}/dataset/${datasetId}/notes` : null;

        return useSWR<DatasetNotesDto>(url, getData);
    },
    useSaveNotes: (studyRef: string, datasetId?: number) => {
        return useSWRMutation(
            `CentralMonitoring/${studyRef}/dataset/${datasetId}/notes`,
            (url: string, { arg }: { arg: DatasetNotesDto }) => {
                return postData<DatasetNotesDto, NonNullable<unknown>>(url, { arg, timeout: 30000 });
            },
        );
    },

    useGetAutomation: (studyRef: string, datasetId: number | undefined, automationType?: AutomationTypes) => {
        let url: string | null = null;

        switch (automationType) {
            case AutomationTypes.Controls:
                url = datasetId ? endpoints.getControlsAutomation(studyRef, datasetId) : null;
                break;
            case AutomationTypes.Site:
            default:
                url = datasetId ? endpoints.getSiteAutomation(studyRef, datasetId) : null;
                break;
        }

        return useSWR<DatasetAutomationDetails, Error>(url, getData);
    },
    invalidateGetAutomation: (studyRef: string, datasetId: number, automationType?: AutomationTypes) => {
        switch (automationType) {
            case AutomationTypes.Controls:
                return mutate(endpoints.getControlsAutomation(studyRef, datasetId));
            case AutomationTypes.Site:
            default:
                return mutate(endpoints.getSiteAutomation(studyRef, datasetId));
        }
    },

    useGetGrid: (studyRef: string, automationType?: AutomationTypes) => {
        switch (automationType) {
            case AutomationTypes.Controls:
                return useSWR<DatasetDetails[], Error>(endpoints.getControlsGrid(studyRef), getData);
            case AutomationTypes.Site:
                return useSWR<DatasetDetails[], Error>(endpoints.getSiteGrid(studyRef), getData);
            default:
                return useSWR<DatasetDetails[], Error>(endpoints.getGrid(studyRef), getData);
        }
    },
    invalidateGetGrid: (studyRef: string, automationType?: AutomationTypes) => {
        switch (automationType) {
            case AutomationTypes.Controls:
                return mutate(endpoints.getControlsGrid(studyRef));
            case AutomationTypes.Site:
                return mutate<DatasetDetails[], Error>(endpoints.getSiteGrid(studyRef));
            default:
                return mutate<DatasetDetails[], Error>(endpoints.getGrid(studyRef));
        }
    },

    useGetSites: (studyRef: string, datasetId: number, skip = false) => {
        return useSWR<Site[], Error>(!skip && endpoints.getSites(studyRef, datasetId), getData);
    },

    useGetDataProcessingLogs: (studyRef: string) => {
        return useSWR<DataProcessingLogDto[], Error>(endpoints.getDataProcessingLogs(studyRef), getData);
    },

    useGetImporterLogs: (studyRef: string, datasetId?: number) => {
        return useSWR<DatasetImportLogDto[], Error>(endpoints.getImportLogs(studyRef, datasetId), getData);
    },

    useDownloadImporterLog: (studyRef: string) => {
        return useSWRMutation<
            DownloadFileResult,
            ValidationErrorResponse,
            string | null,
            { logType: number; datasetId: number }
        >(`downloadImportLog/${studyRef}`, async (key, data) => {
            const url = endpoints.downloadImportLog(studyRef, data.arg.logType, data.arg.datasetId);
            const response = await getDataForFile(url, { ...data, timeout: 30000 });
            // Process response to get file name from headers
            const cdisp = response.headers.get('Content-Disposition');
            const reg: RegExp = /filename="(.*)"/;
            const match = cdisp?.match(reg);
            const fileName = match ? match[1] : null;
            const blob = await response.blob();
            // Return formed data to caller
            return { fileName, blob };
        });
    },
};

export default DatasetApi;
